<template>
  <div class="coupon-property-pool-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('couponPropertyPoolDetail.list.title')"
      :subtitle="$t('couponPropertyPoolDetail.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.cppId" clearable
            :placeholder="$t('couponPropertyPoolDetail.search.cppId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.couponName" clearable
            :placeholder="$t('couponPropertyPoolDetail.search.couponName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.fromType" clearable class="w-full"
            :placeholder="$t('couponPropertyPoolDetail.search.fromType')">
            <el-option :label="$t('couponPropertyPoolDetail.search.fromTypePlaceholder')" value="" />
            <el-option :label="$t('couponPropertyPoolDetail.search.fromType1')" value="1001" />
            <el-option :label="$t('couponPropertyPoolDetail.search.fromType2')" value="2002" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.userName" clearable
            :placeholder="$t('couponPropertyPoolDetail.search.userName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.tel" clearable
            :placeholder="$t('couponPropertyPoolDetail.search.tel')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.toType" clearable class="w-full"
            :placeholder="$t('couponPropertyPoolDetail.search.toType')">
            <el-option :label="$t('couponPropertyPoolDetail.search.toTypePlaceholder')" value="" />
            <el-option :label="$t('couponPropertyPoolDetail.search.toType1')" value="1011" />
            <el-option :label="$t('couponPropertyPoolDetail.search.toType2')" value="3003" />
            <el-option :label="$t('couponPropertyPoolDetail.search.toType3')" value="4004" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('couponPropertyPoolDetail.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('couponPropertyPoolDetail.page.empty')" @retry="getList">
        <el-table-column prop="cppId" :label="$t('couponPropertyPoolDetail.table.cppId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.cppId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="couponName" :label="$t('couponPropertyPoolDetail.table.couponName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="value" :label="$t('couponPropertyPoolDetail.table.value')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.value }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sendCount" :label="$t('couponPropertyPoolDetail.table.sendCount')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.sendCount }}{{ $t('couponPropertyPoolDetail.table.unit') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="userName" :label="$t('couponPropertyPoolDetail.table.userName')" align="center" width="120" />
        <el-table-column prop="tel" :label="$t('couponPropertyPoolDetail.table.tel')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('couponPropertyPoolDetail.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleReturn(scope.row)">
              {{ $t('couponPropertyPoolDetail.btn.return') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 退回确认对话框 -->
    <delete-coupon-property-pool-detail ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { listCouponPropertyPoolDetail } from '@/api/scm/couponPropertyPoolDetailApi'
import DeleteCouponPropertyPoolDetail from '@/components/scm/deleteCouponPropertyPoolDetail'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponPropertyPoolDetailList',
  components: {
    DeleteCouponPropertyPoolDetail,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        cppId: '',
        couponName: '',
        fromType: '',
        toType: '',
        userName: '',
        tel: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.page.current,
          row: this.page.size
        }
        const { data, total } = await listCouponPropertyPoolDetail(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询优惠券赠送明细失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('couponPropertyPoolDetail.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.cppId = ''
      this.searchForm.couponName = ''
      this.searchForm.fromType = ''
      this.searchForm.toType = ''
      this.searchForm.userName = ''
      this.searchForm.tel = ''
      this.page.current = 1
      this.getList()
    },
    handleReturn(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-property-pool-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
